import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { rub } from '@/lib/currency';
import { useSelectedSubscription } from '@/lib/use-selected-subscription';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton, SkeletonCard, SkeletonLine } from '@/components/ui/skeleton';
import { OptionRow, OptionGroup } from '@/components/ui/calm';
import { Rocket, Gem } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { getWebApp, haptic, confirmAction, setMainButtonText } from '@/lib/telegram';
import { toast } from 'sonner';
import { normalizePeriodDays, periodLabel } from '@/lib/period';

export const Route = createFileRoute('/upgrade')({
  component: Upgrade,
});

function Upgrade() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);

  const { data: plans, isPending: plansPending } = useQuery({
    queryKey: ['plans', 'upgrade'],
    queryFn: () => api.plans('term', 'upgrade'),
  });
  const { sub, isPending: subPending } = useSelectedSubscription();

  // Пробник не апгрейдится: у трёхдневного периода нет тарифов побольше, и экран
  // советовал «дождаться окончания подписки и купить новую» человеку, который пришёл
  // платить. Ведём сразу в тарифы: сюда попадают из тостов и плитки «Улучшить».
  useEffect(() => {
    // «Навсегда» апгрейдить не во что, её пробник ведёт в свой раздел.
    if (sub?.supplier === 'scvpn') {
      navigate(
        sub.isTrial
          ? { to: '/plans', search: { section: 'lifetime' }, replace: true }
          : { to: '/me', replace: true },
      );
      return;
    }
    if (sub?.isTrial) navigate({ to: '/plans', replace: true });
  }, [sub?.isTrial, sub?.supplier, navigate]);

  // Apgrade в AdaptGroup работает только внутри того же бизнес-периода. AG отдаёт
  // одинаковый «1 год» как 360 (Pro/Premium) и 365 (Standard) — сравниваем по
  // каноническому периоду, иначе пользователь не увидит апгрейда даже когда он есть.
  const subCanonical = sub ? normalizePeriodDays(sub.days) : null;
  const upgradablePlans = (plans || []).filter(
    (p) =>
      sub &&
      subCanonical !== null &&
      // Спецтариф не может быть целью апгрейда: он покупается один раз, и переезд
      // на него обошёл бы этот лимит. Бэк отбивает такой апгрейд 409.
      !p.isSpecial &&
      normalizePeriodDays(p.days) === subCanonical &&
      p.devices > sub.devices &&
      p.id !== sub.planId,
  );

  // Цену доплаты знает только бэк: она зависит от остатка дней текущего тарифа.
  // Спрашиваем за каждый кандидат сразу (их один-два на период). Раньше доплата
  // показывалась только у выбранной строки, а у остальных стояла полная цена тарифа,
  // и в одной колонке «Premium 194 ₽» соседствовал с «Pro 299 ₽»: партнёры
  // принимали это за сломанные цены.
  const quotes = useQueries({
    queries: upgradablePlans.map((plan) => ({
      queryKey: ['upgradeQuote', sub?.id, plan.id],
      queryFn: () => api.upgradeQuote(sub!.id, plan.id),
      enabled: Boolean(sub?.id),
      staleTime: 60_000,
    })),
  });
  const quoteByPlan = new Map(upgradablePlans.map((plan, i) => [plan.id, quotes[i]]));

  const labelFor = (q: (typeof quotes)[number] | undefined) => {
    const data = q?.data;
    // Ответ без суммы (сбой на бэке, старая версия) не превращаем в «NaN undefined».
    if (!data || typeof data.charged_display_minor !== 'number' || !data.display_currency) return null;
    return data.display_currency === 'RUB'
      ? rub(Math.round(data.charged_display_minor / 100))
      : `${(data.charged_display_minor / 100).toFixed(2)} ${data.display_currency}`;
  };
  const selectedQuote = selectedPlanId ? quoteByPlan.get(selectedPlanId) : undefined;
  const chargeLabel = labelFor(selectedQuote);
  const quoteLoading = Boolean(selectedQuote?.isFetching && !selectedQuote.data);

  // Замок на повторный тап по MainButton — см. checkout.$planId.tsx. Тут окно особенно
  // широкое: confirmAction ждёт ответа в нативном попапе Telegram.
  const upgradeBusyRef = useRef(false);

  const { mutate: doUpgrade, isPending } = useMutation({
    mutationFn: ({ subId, planId }: { subId: string; planId: string }) =>
      api.upgradeSubscription(subId, planId),
    onSettled: () => {
      upgradeBusyRef.current = false;
    },
    onSuccess: () => {
      haptic('success');
      qc.invalidateQueries({ queryKey: ['subs'] });
      qc.invalidateQueries({ queryKey: ['profile'] });
      toast.success(t('Upgrade done'));
      navigate({ to: '/me' });
    },
    onError: (err: Error) => {
      haptic('error');
      const msg = err.message || '';
      if (msg.toLowerCase().includes('insufficient') || msg.includes('402') || msg.includes('баланс')) {
        toast.error(t('Upgrade no balance'), { duration: 6000 });
      } else if (msg.toLowerCase().includes('plan not')) {
        toast.error(t('Upgrade plan gone'));
      } else {
        toast.error(msg || t('Upgrade failed'));
      }
    },
  });

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton || !tg?.MainButton) return;
    tg.BackButton.show();
    const onBack = () => navigate({ to: '/subscription' });
    tg.BackButton.onClick(onBack);

    const selected = plans?.find((p) => p.id === selectedPlanId);
    if (selected && sub) {
      setMainButtonText(
        chargeLabel ? `${selected.name} · ${chargeLabel}` : t('Upgrade to', { plan: selected.name }),
      );
      tg.MainButton.show();
      if (isPending || quoteLoading) tg.MainButton.showProgress();
      else tg.MainButton.hideProgress();

      const onMain = async () => {
        if (upgradeBusyRef.current) return;
        upgradeBusyRef.current = true;
        const ok = await confirmAction(
          chargeLabel
            ? t('Upgrade confirm exact', { plan: selected.name, amount: chargeLabel })
            : t('Upgrade confirm', { plan: selected.name }),
          t('Upgrade confirm title'),
        );
        if (!ok) {
          upgradeBusyRef.current = false;
          return;
        }
        doUpgrade({ subId: sub.id, planId: selected.id });
      };
      tg.MainButton.onClick(onMain);
      return () => {
        tg.MainButton.offClick(onMain);
        tg.MainButton.hide();
        tg.MainButton.hideProgress();
        tg.BackButton.offClick(onBack);
      };
    }
    tg.MainButton.hide();
    return () => tg.BackButton.offClick(onBack);
  }, [navigate, selectedPlanId, plans, sub, t, isPending, doUpgrade, chargeLabel, quoteLoading]);

  // Пока планы и подписка едут, список апгрейдов пуст, и экран уверенно сообщал
  // «апгрейд недоступен» — причём с прочерком вместо названия текущего тарифа.
  const loading = plansPending || subPending;

  return (
    <div className="p-4 space-y-6 pb-24">
      <h2 className="tl-h text-[32px] font-semibold leading-tight tracking-[-0.035em] mb-2 px-1 pt-3 text-foreground">{t('Upgrade Plan')}</h2>
      {/* div, а не p: блочный скелетон внутри абзаца ломает разметку (браузер
          закрывает <p> раньше времени, React ругается на гидрацию). */}
      <div className="text-[var(--t-body)] px-2 flex items-center gap-2">
        {t('Upgrade current plan')}:{' '}
        {sub ? (
          <b className="text-foreground">{sub.planName}</b>
        ) : (
          <SkeletonLine size="lg" className="w-24" />
        )}
      </div>
      <p className="text-xs text-[var(--t-muted)] px-2">
        {t('Upgrade rule')}
      </p>

      {loading ? (
        <div className="space-y-2">
          <SkeletonCard className="h-[68px] min-h-0 rounded-xl" />
          <SkeletonCard className="h-[68px] min-h-0 rounded-xl" />
        </div>
      ) : upgradablePlans.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-[var(--t-muted)]">
            {t('Upgrade none title', { period: sub ? periodLabel(sub.days) : '—' })}{' '}
            {t('Upgrade none desc')}
          </CardContent>
        </Card>
      ) : (
        <OptionGroup>
          {upgradablePlans.map((plan) => (
            <OptionRow
              key={plan.id}
              icon={plan.devices <= 5 ? Rocket : Gem}
              // Значок партнёра приписываем к названию, как в кнопке самого бота:
              // у строки апгрейда иконка слева одна на все тарифы и несёт уровень.
              title={plan.emoji ? `${plan.emoji} ${plan.name}` : plan.name}
              subtitle={
                plan.trafficGb !== null
                  ? t('Plan devices unlim gb', {
                      devices: t('Count devices', { count: plan.devices }),
                      gb: plan.trafficGb,
                    })
                  : t('Plan devices unlim', { devices: t('Count devices', { count: plan.devices }) })
              }
              trailing={
                (() => {
                  const q = quoteByPlan.get(plan.id);
                  const label = labelFor(q);
                  if (label) {
                    return (
                      <span className="flex flex-col items-end leading-tight">
                        <span className="font-semibold tabular text-foreground">{label}</span>
                        <span className="text-[11px] text-[var(--t-muted)]">{t('Upgrade surcharge')}</span>
                      </span>
                    );
                  }
                  if (q?.isError || q?.data) {
                    return (
                      <span className="font-semibold tabular text-foreground">{rub(plan.priceRub)}</span>
                    );
                  }
                  return <Skeleton className="h-5 w-14 rounded-md" />;
                })()
              }
              selected={selectedPlanId === plan.id}
              onSelect={() => setSelectedPlanId(plan.id)}
            />
          ))}
        </OptionGroup>
      )}
    </div>
  );
}
